import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Table, TableWrapper } from "@/components/ui/table";
import { TableState } from "@/components/data-display/table-state";
import { PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { fetchAdminRoles, type AdminRoleDefinition } from "@/lib/api";
import type { PermissionLevel, RoleDefinition } from "@/types";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { classifyFailure, legacyErrorReason } from "@/lib/api-failure";
import { hasPermission, roleDisplayLabel } from "@/lib/permissions";

/**
 * The roles that may read this catalog, derived from the permission catalog in
 * `@/lib/permissions` — the same `role:read` key `GET /v1/admin/roles` is gated
 * on. A denial names their display labels (e.g. "Organization Admin"), never the
 * raw role ids or scope strings an administrator cannot act on (D07/IAM-139).
 */
const ROLE_CATALOG_READER_ROLES = (
  [
    "org-admin",
    "security-admin",
    "developer",
    "wallet-admin",
    "wallet_operator",
    "compliance-officer",
    "policy_approver",
    "treasury-operator",
    "finance_operator",
    "finance_viewer",
    "auditor",
    "support-readonly",
  ] as const
).filter((role) => hasPermission(role, "role:read"));

const ROLE_CATALOG_READER_LABEL = ROLE_CATALOG_READER_ROLES.map(roleDisplayLabel).join(" or ");

const RESOURCE_COLUMNS: string[] = [
  "Users",
  "Roles",
  "Groups",
  "Service Accounts",
  "Audit Log",
  "Wallets",
  "Transfers",
  "Policies",
  "Deployment",
];

const PERM_CLASS: Record<PermissionLevel, string> = {
  Full: "font-bold text-success",
  Read: "font-semibold text-info",
  Create: "font-semibold text-warning",
  Approve: "font-semibold text-warning",
  Own: "font-semibold text-primary",
  Use: "font-semibold text-info",
  Staging: "font-semibold text-warning",
  Scoped: "font-semibold text-muted",
  Keys: "font-semibold text-info",
  Dev: "font-semibold text-info",
  None: "text-subtle",
};

const SCOPE_TO_RESOURCE: Record<string, string> = {
  WALLETS_READ: "Wallets",
  WALLETS_WRITE: "Wallets",
  BALANCES_READ: "Wallets",
  TRANSFERS_READ: "Transfers",
  TRANSFERS_WRITE: "Transfers",
  TRANSFERS_DRAFT: "Transfers",
  TRANSFERS_ESTIMATE: "Transfers",
  APPROVALS_READ: "Policies",
  APPROVALS_APPROVE: "Policies",
  APPROVALS_REJECT: "Policies",
  POLICIES_READ: "Policies",
  POLICIES_WRITE: "Policies",
  RECONCILIATION_READ: "Audit Log",
  RECONCILIATION_MANAGE: "Audit Log",
  RECONCILIATION_EXPORT: "Audit Log",
  AUDIT_READ: "Audit Log",
  AUDIT_EXPORT: "Audit Log",
  CONFIG_READ: "Deployment",
  CONFIG_WRITE: "Deployment",
  ADMIN_USERS_READ: "Users",
  ADMIN_USERS_WRITE: "Users",
  ADMIN_USERS_DEACTIVATE: "Users",
  AUTH_ROLES_MANAGE: "Roles",
  AUTH_SESSIONS_VERIFY: "Groups",
  AUTH_SESSIONS_MANAGE: "Groups",
  NOTIFICATIONS_READ: "Service Accounts",
  NOTIFICATIONS_WRITE: "Service Accounts",
};

const WRITE_SCOPES = new Set([
  "WALLETS_WRITE", "TRANSFERS_WRITE", "TRANSFERS_DRAFT", "APPROVALS_APPROVE",
  "APPROVALS_REJECT", "POLICIES_WRITE", "RECONCILIATION_MANAGE",
  "RECONCILIATION_EXPORT", "AUDIT_EXPORT", "CONFIG_WRITE", "ADMIN_USERS_WRITE",
  "ADMIN_USERS_DEACTIVATE", "AUTH_ROLES_MANAGE", "AUTH_SESSIONS_MANAGE",
  "NOTIFICATIONS_WRITE",
]);

function derivePermissionLevel(scopes: string[], resource: string): PermissionLevel {
  const resourceScopes = scopes.filter(s => SCOPE_TO_RESOURCE[s] === resource);
  if (resourceScopes.length === 0) return "None";
  const hasWrite = resourceScopes.some(s => WRITE_SCOPES.has(s));
  if (hasWrite) return "Full";
  return "Read";
}

/**
 * A catalog row plus the grant counts the matrix cannot express. A role that
 * grants nothing derives `None` in every resource column, which reads as a row
 * of nine identical "None" cells; the counts let the row say "No permissions
 * granted" once instead. Kept local to this feature (an `AdminRoleDefinition`
 * carries them already) so no shared type has to grow a field only this page
 * needs.
 */
type CatalogRole = RoleDefinition & { scopeCount: number; permissionCount: number };

function apiRoleToRoleDefinition(role: AdminRoleDefinition): CatalogRole {
  const permissions: Record<string, PermissionLevel> = {};
  for (const col of RESOURCE_COLUMNS) {
    permissions[col] = derivePermissionLevel(role.scopes, col);
  }
  return {
    id: role.roleName,
    label: role.displayName || role.roleName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    description: `Scopes: ${role.scopes.length} · Permissions: ${role.permissions.length}`,
    permissions,
    scopeCount: role.scopes.length,
    permissionCount: role.permissions.length,
  };
}

export function RoleCatalog() {
  const plainError = usePlainErrorMessage();
  const [roles, setRoles] = useState<CatalogRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supportReference, setSupportReference] = useState<string | null>(null);
  // Only the newest request may write state: a retry that lands before the
  // first response would otherwise let a stale failure overwrite fresh rows.
  const requestId = useRef(0);

  const load = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setSupportReference(null);
    fetchAdminRoles()
      .then((result) => {
        if (id !== requestId.current) return;
        if (result.error) {
          setError(legacyErrorReason(result.error));
          setSupportReference(result.supportReference ?? null);
        } else {
          setRoles(result.roles.map(apiRoleToRoleDefinition));
        }
        setLoading(false);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setError("NETWORK_ERROR");
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeRoles = useMemo(() => roles, [roles]);
  const columnCount = RESOURCE_COLUMNS.length + 1;
  // A 401/403 is a permission state, not a failure to retry: the same request
  // will be refused again, so the denial gets its own calm panel and no Retry.
  const denied = error !== null && classifyFailure({ reasonCode: error }) === "denied";

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Role Catalog</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Role Catalog</h1>
        <p className="mt-1 text-sm text-muted">
          Permission matrix across every system role. Read-only — role permissions are fixed by the platform and cannot be edited here.
        </p>
      </div>

      {!loading && denied ? (
        <div>
          <PermissionDeniedState
            title="Access required"
            description={`Viewing roles needs the ${ROLE_CATALOG_READER_LABEL} role. Ask an organization admin to assign it.`}
          />
          <SupportDetails reference={supportReference} className="mt-3" />
        </div>
      ) : (
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="sticky left-0 z-20 min-w-[220px] bg-bg px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Role</th>
              {RESOURCE_COLUMNS.map((col) => (
                <th key={col} className="whitespace-nowrap px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeRoles.map((role) => (
              <tr key={role.id} className="border-b border-line last:border-b-0">
                <td className="sticky left-0 z-10 bg-surface px-4 py-[11px] align-top">
                  <div className="font-mono text-[13px] font-semibold text-ink">{role.label}</div>
                  {/* The role id an admin assigns and an API or audit record names; two roles can
                      carry near-identical display names (ALL-5690). */}
                  <div className="mt-0.5 font-mono text-[11px] text-muted" data-testid="role-id">{role.id}</div>
                  {/* A zero-grant role says "No permissions granted" once in the
                      matrix below; repeating "Scopes: 0 · Permissions: 0" here
                      would restate the same absence in count form and read as a
                      broken row. The counts line stays for every role that
                      actually grants something. */}
                  {!(role.scopeCount === 0 && role.permissionCount === 0) && (
                    <div className="mt-0.5 max-w-[260px] text-[11.5px] leading-snug text-muted">{role.description}</div>
                  )}
                </td>
                {role.scopeCount === 0 && role.permissionCount === 0 ? (
                  /* A role that grants nothing derives `None` for every resource
                     column, which reads as a wall of identical cells and hides
                     the one fact that matters: this role grants no permission at
                     all. Say it once, in muted copy, instead. */
                  <td
                    colSpan={RESOURCE_COLUMNS.length}
                    className="px-4 py-[11px] text-[12.5px] text-muted"
                    data-testid="role-no-permissions"
                  >
                    No permissions granted
                  </td>
                ) : (
                  RESOURCE_COLUMNS.map((col) => {
                    const level = role.permissions[col] ?? "None";
                    return (
                      <td key={col} className={`whitespace-nowrap px-4 py-[11px] text-[12.5px] ${PERM_CLASS[level]}`}>
                        {level}
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
            {loading && (
              <TableState
                variant="loading"
                colSpan={columnCount}
                title="Loading roles"
              />
            )}
            {/* A failure states the plain-language sentence only — never a
                machine code inline. The server-minted support reference below
                is the one technical handle an admin should hand to support
                (D636). */}
            {!loading && error && (
              <TableState
                variant="error"
                colSpan={columnCount}
                title="Couldn't load the role catalog"
                description={plainError({ reasonCode: error })}
                onRetry={load}
                supportReference={supportReference}
              />
            )}
            {!loading && !error && activeRoles.length === 0 && (
              <TableState
                variant="empty"
                colSpan={columnCount}
                title="No roles in this catalog"
                description="No role definitions have been returned for this tenant, so nobody can be granted access here. Roles are provisioned with the tenant — contact support if this tenant is expected to have roles."
              />
            )}
          </tbody>
        </Table>
      </TableWrapper>
      )}

      {/* The matrix mixes role names with permission words, and the column
          headers are terse module names. One line under the table says what the
          cell values mean, using the same three words the cells render — so a
          reader never has to guess that "Read" is narrower than "Full" or that
          "None" means no access at all. */}
      {!loading && !denied && activeRoles.length > 0 && (
        <p className="mt-2 text-[11.5px] leading-snug text-muted" data-testid="role-matrix-legend">
          Cell values:{" "}
          <span className="font-bold text-success">Full</span> read and write ·{" "}
          <span className="font-semibold text-info">Read</span> view only ·{" "}
          <span className="text-subtle">None</span> no access.
        </p>
      )}
    </div>
  );
}
